import { storeToRefs } from 'pinia';
import { type Component, type Ref, computed, defineAsyncComponent } from 'vue';
import BetConstructLiveStream from '@/components/match/BetConstructLiveStream.vue';
import BetGeniusLiveStream from '@/components/match/BetGeniusLiveStream.vue';
import BetRadarStream from '@/components/match/BetRadarStream.vue';
import GLiveStream from '@/components/match/GLiveStream.vue';
import TwitchLiveStream from '@/components/match/TwitchLiveStream.vue';
import type { IMatch } from '@/interface/IMatch';
import { BetRadarStreamStatus, LiveStreamResource, PlayerType, SportType } from '@/interface/enum';
import { LicenseType } from '@/interface/pollux';
import Api from '@/libraries/api';
import { isSoccer } from '@/libraries/helpers/sportsHelper';
import { getHost } from '@/libraries/url';
import { useAccountStore } from '@/stores/accountStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useRootStore } from '@/stores/rootStore';

const VideoJsLiveStream = defineAsyncComponent(() => import('@/components/match/VideoJsLiveStream.vue'));

export interface LiveStreamComponent {
    component: Component;
    props: Record<string, any>;
}

export function useLiveStream(match: Ref<IMatch | null>) {
    const {
        isGLiveStreamEnabled,
        gLiveStreamDomainBlacklist,
        gLiveStreamLeagueIdBlacklist,
        gLiveStreamRestrictions,
        isInBetRadarLiveStreamWhiteList,
    } = storeToRefs(useFeatureToggleStore());

    const { countryCode, brandName } = storeToRefs(useRootStore());
    const { customerInfo } = storeToRefs(useAccountStore());

    const isBetRadarLiveStreamAvailable = computed(() => {
        if (!match.value?.stream?.betRadar) return false;

        const isBetRadarStreamOnAir = match.value.stream?.betRadar?.streamStatus === BetRadarStreamStatus.OnAirBroadcast;
        if (!isBetRadarStreamOnAir) return false;

        return isInBetRadarLiveStreamWhiteList.value
            || match.value.stream.betRadar.allowedCountries.includes(countryCode.value);
    });

    const isGLiveValidRestrictionGroups = computed(() => gLiveStreamRestrictions.value
        .map(rawGroup => parseGLiveGroupRestrict(rawGroup))
        .filter(group => validate(group.licenses, LicenseType[customerInfo.value.license])
                && validate(group.userTypes, PlayerType[customerInfo.value.playerType])
                && validate(group.brandTypes, brandName.value)
                && validate(group.currencies, customerInfo.value.currency)
                && validate(group.countries, countryCode.value)));

    const isGLiveStreamInBlacklist = computed(() => ((gLiveStreamDomainBlacklist.value.includes(getHost()) || gLiveStreamDomainBlacklist.value.includes('*'))
        && (gLiveStreamLeagueIdBlacklist.value.includes(String(match.value?.leagueId)) || gLiveStreamLeagueIdBlacklist.value.includes('*'))));

    const isGLiveStreamInWhitelist = computed(() => isGLiveValidRestrictionGroups.value.some(group => validate(group.leagueSizes, match.value?.tournament?.tournamentSize ?? '')));

    const isGLiveStreamAvailable = computed(() => {
        if (!isGLiveStreamEnabled.value || !match.value?.stream?.gLive) return false;
        if (!isGLiveStreamInWhitelist.value || isGLiveStreamInBlacklist.value) return false;
        if (isSoccer(match.value?.sportType)) return true;
        return !!match.value?.stream?.gLive?.isPlaying;
    });

    const isTwitchLiveStreamAvailable = computed(() => !!match.value?.stream?.videoUrl?.includes('player.twitch.tv'));

    const isMuayThaiLiveStreamAvailable = computed(() => match.value?.stream?.videoUrl
    && customerInfo.value.license === LicenseType.MNL
    && match.value?.sportType === SportType.MuayThai
    && !isTwitchLiveStreamAvailable.value);

    const isBetConstructLiveStreamAvailable = computed(() => !!(match.value?.stream?.betConstruct?.allowedCountries.includes(countryCode.value)));

    const isBetGeniusLiveStreamAvailable = computed(() => !!(match.value?.stream?.betGenius?.allowedCountries.includes(countryCode.value))
    && match.value.stream?.betGenius?.isPlaying && !match.value?.isClosed);

    const isLiveStreamPlayable = computed(() => !!((match.value?.isLive || (match.value && isSoccer(match.value.sportType) && !match.value.isSimulation))
        && (isTwitchLiveStreamAvailable.value
            || isMuayThaiLiveStreamAvailable.value
            || isBetRadarLiveStreamAvailable.value
            || isGLiveStreamAvailable.value
            || isBetConstructLiveStreamAvailable.value
            || isBetGeniusLiveStreamAvailable.value)));

    // PiP is only supported for providers with a stable, embeddable video URL (see getVideoUrlForPip).
    const isPipSupportedStreamAvailable = computed(() => isTwitchLiveStreamAvailable.value
        || isGLiveStreamAvailable.value
        || isBetConstructLiveStreamAvailable.value);

    const liveStreamComponent = computed((): LiveStreamComponent | null => {
        if (!match.value || !isLiveStreamPlayable.value) return null;

        if (isTwitchLiveStreamAvailable.value) {
            return {
                component: TwitchLiveStream,
                props: {
                    videoUrl: match.value.stream!.videoUrl!,
                },
            };
        }

        if (isMuayThaiLiveStreamAvailable.value) {
            return {
                component: VideoJsLiveStream,
                props: {
                    key: match.value.stream!.videoUrl!,
                    videoUrl: match.value.stream!.videoUrl!,
                },
            };
        }

        if (isBetRadarLiveStreamAvailable.value) {
            return {
                component: BetRadarStream,
                props: {
                    matchId: match.value.eventId,
                    streamId: match.value.stream!.betRadar!.streamId,
                },
            };
        }

        if (isGLiveStreamAvailable.value) {
            return {
                component: GLiveStream,
                props: {
                    matchId: match.value.eventId,
                },
            };
        }

        if (isBetConstructLiveStreamAvailable.value) {
            return {
                component: BetConstructLiveStream,
                props: {
                    matchId: match.value.eventId,
                },
            };
        }

        if (isBetGeniusLiveStreamAvailable.value) {
            return {
                component: BetGeniusLiveStream,
                props: {
                    matchId: match.value.eventId,
                    providerMatchId: match.value.stream!.betGenius!.providerEventId,
                },
            };
        }

        return null;
    });

    async function getVideoUrlForPip(): Promise<{ url: string, provider: string } | null> {
        if (!match.value || !isPipSupportedStreamAvailable.value) return null;

        if (isTwitchLiveStreamAvailable.value && match.value.stream?.videoUrl) {
            return { url: `${match.value.stream.videoUrl}&parent=${window.location.host}&autoplay=true`, provider: 'Twitch' };
        }
        if (isGLiveStreamAvailable.value) {
            const result = await Api.getLiveStream(match.value.eventId, LiveStreamResource.GLive);
            if (result.errorCode === 0 && result.url) return { url: result.url, provider: 'GLive' };
        }
        if (isBetConstructLiveStreamAvailable.value) {
            const result = await Api.getLiveStream(match.value.eventId, LiveStreamResource.BetConstruct);
            if (result.errorCode === 0 && result.url) return { url: result.url, provider: 'BetConstruct' };
        }
        return null;
    }

    return {
        isLiveStreamPlayable,
        isMuayThaiLiveStreamAvailable,
        isPipSupportedStreamAvailable,
        liveStreamComponent,
        getVideoUrlForPip,
    };
}

function parseGLiveGroupRestrict(rawGroup: string) {
    // rawGroup format = `License:UserType:Currency:IpCountry:LeagueSize:BrandType`
    const [licenses, userTypes, currencies, countries, leagueSizes, brandTypes] = rawGroup?.toUpperCase().split(':') ?? [];
    return {
        licenses: licenses?.split(',') ?? [],
        userTypes: userTypes?.split(',') ?? [],
        currencies: currencies?.split(',') ?? [],
        countries: countries?.split(',') ?? [],
        leagueSizes: leagueSizes?.split(',') ?? [],
        brandTypes: brandTypes?.split(',') ?? [],
    };
}

function validate(restrictions: string[], value: string) {
    return restrictions.includes('*') || restrictions.includes(value.toUpperCase());
}
